import { defineStore } from 'pinia'
import { type Ref, ref } from 'vue'

import type {
  FrappeCallRequestDeleteParams,
  FrappeCallRequestDeletePath,
  FrappeCallRequestGetParams,
  FrappeCallRequestGetPath,
  FrappeCallRequestPostParams,
  FrappeCallRequestPostPath,
  FrappeCallRequestPutParams,
  FrappeCallRequestPutPath,
  FrappeCallTokenReturnValue,
} from '@/plugins/vuePluginFrappe'

import {
  type FrappeCallInstance,
  getFrappeCallInstance,
  transformFrappeErrorObject,
} from '@/utilities/stores'

type CallInstanceAuthTokenType = FrappeCallInstance['tokenType']
type CallInstanceAxiosInstance = FrappeCallInstance['axios']
type CallInstanceUseAuthToken = FrappeCallInstance['useToken']

type DeleteRequestParams = FrappeCallRequestDeleteParams
type DeleteRequestPath = FrappeCallRequestDeletePath
type GetRequestParams = FrappeCallRequestGetParams
type GetRequestPath = FrappeCallRequestGetPath
type PostRequestParams = FrappeCallRequestPostParams
type PostRequestPath = FrappeCallRequestPostPath
type PutRequestParams = FrappeCallRequestPutParams
type PutRequestPath = FrappeCallRequestPutPath

type DeleteRequestFunction = <DeleteResponseType>(
  functionName: DeleteRequestPath,
  parameters: DeleteRequestParams,
) => Promise<DeleteResponseType>
type GetRequestFunction = <GetResponseType>(
  functionName: GetRequestPath,
  parameters: GetRequestParams,
) => Promise<GetResponseType>
type GetCallInstanceAuthTokenFunction = () => FrappeCallTokenReturnValue
type PostRequestFunction = <PostResponseType>(
  functionName: PostRequestPath,
  parameters: PostRequestParams,
) => Promise<PostResponseType>
type PutRequestFunction = <PutResponseType>(
  functionName: PutRequestPath,
  parameters: PutRequestParams,
) => Promise<PutResponseType>

interface FrappeCallAPIStore {
  callInstanceAuthTokenType: Ref<CallInstanceAuthTokenType>
  callInstanceAxiosInstance: Ref<CallInstanceAxiosInstance>
  delete: DeleteRequestFunction
  get: GetRequestFunction
  getCallInstanceAuthToken: GetCallInstanceAuthTokenFunction
  isCallInstanceUseAuthToken: Ref<CallInstanceUseAuthToken>
  post: PostRequestFunction
  put: PutRequestFunction
}

/**
 * ! Caution:
 * * all "frappeInstance" function calls must be wrapped by
 * * "transformFrappeErrorObject" function, for consistent error handling
 */

const useFrappeCallAPIStore = defineStore('frappeCallAPI', (): FrappeCallAPIStore => {
  const frappeCallInstance: FrappeCallInstance = getFrappeCallInstance()

  const callInstanceAuthTokenType = ref<CallInstanceAuthTokenType>(frappeCallInstance.tokenType)
  const callInstanceAxiosInstance = ref<CallInstanceAxiosInstance>(frappeCallInstance.axios)
  const isCallInstanceUseAuthToken = ref<CallInstanceUseAuthToken>(frappeCallInstance.useToken)

  const deleteRequestFunction: DeleteRequestFunction = async (functionName, parameters) => {
    return await transformFrappeErrorObject(async () => {
      return await frappeCallInstance.delete(functionName, parameters)
    })
  }

  const getRequestFunction: GetRequestFunction = async (functionName, parameters) => {
    return await transformFrappeErrorObject(async () => {
      return await frappeCallInstance.get(functionName, parameters)
    })
  }

  const getCallInstanceAuthToken: GetCallInstanceAuthTokenFunction = () => {
    let callInstanceTokenValue: string | undefined = undefined

    if (frappeCallInstance.token) {
      callInstanceTokenValue = frappeCallInstance.token()
    }

    return callInstanceTokenValue
  }

  const postRequestFunction: PostRequestFunction = async (functionName, parameters) => {
    return await transformFrappeErrorObject(async () => {
      return await frappeCallInstance.post(functionName, parameters)
    })
  }

  const putRequestFunction: PutRequestFunction = async (functionName, parameters) => {
    return await transformFrappeErrorObject(async () => {
      return await frappeCallInstance.put(functionName, parameters)
    })
  }

  return {
    callInstanceAuthTokenType,
    callInstanceAxiosInstance,
    delete: deleteRequestFunction,
    get: getRequestFunction,
    getCallInstanceAuthToken,
    isCallInstanceUseAuthToken,
    post: postRequestFunction,
    put: putRequestFunction,
  }
})

export {
  type GetRequestParams,
  type GetRequestPath,
  type PostRequestParams,
  type PostRequestPath,
  type PutRequestParams,
  type PutRequestPath,
  useFrappeCallAPIStore,
}
